import { useQuery } from "@apollo/client/react";
import { GetAutomationCatalogDocument } from "#app/lib/data";
import type { Omittable } from "@salestrak/ui";

// A single catalog field on a config schema entry (server's ConfigField).
export type AutomationConfigField = Omittable<{
  key: string;
  label: string;
  type:
    | "string"
    | "richText"
    | "number"
    | "boolean"
    | "select"
    | "entityRef"
    | "duration"
    | "json"
    | "condition"
    // Semantic scalar types on context/output schemas — they type variables
    // for the picker and the publish gate's compatibility check.
    | "date"
    | "email"
    | "url";
  required?: boolean;
  templatable?: boolean;
  // Restricts which variable TYPES this templatable field accepts (e.g. the
  // email "To" field only takes email variables). Unset = any.
  variableTypes?: string[];
  // Accepts ONLY a whole variable: renders a variable picker rather than a text
  // box, and the field is hidden when no variable of an accepted type exists.
  variablesOnly?: boolean;
  // Other field keys this one cannot be set alongside. Setting this field clears
  // them, so the invalid combination is unreachable rather than rejected later.
  exclusiveWith?: string[];
  // An option's `example` is a display-only sample of the data choosing it
  // produces at run time (the webhook payload preview reads it).
  options?: { value: string; label: string; example?: unknown }[];
  // Choices that live in the CONNECTED ACCOUNT rather than in code. Present
  // means "render a picker fed by
  // integrationRemoteOptions", and `key` is the provider-owned collection name
  // to ask for. Deliberately separate from `options`, which is static and
  // membership-validated at publish; see the server's ConfigFieldOptionSource.
  // `multiple` allows several picks. The VALUE stays a string holding them
  // comma-joined — there is no array `ConfigFieldType`, and adding one to serve
  // a single field would have to reach validation, the compatibility gate and
  // every renderer. Safe as a separator because these are provider-issued ids.
  // `multipleKinds` names which option KINDS may be picked more than once;
  // kinds are never mixed and anything else is single-pick. The picker enforces
  // it as the author selects, and the provider re-checks server-side — one
  // declaration, both sides.
  optionSource?: { key: string; multiple?: boolean; multipleKinds?: string[] };
  entityType?: string;
  description?: string;
  // Display-only sample value on context/output schema fields — shown in the
  // editor's "Available variables" reference. Never used at run time.
  example?: string;
}>;

// One entry in the server's step-type catalog — its key, label, icon, category
// and config schema. Taken from the query rather than restated, so the shape
// follows the schema and cannot drift.
export type StepTypeOption =
  ReturnType<typeof useAutomationCatalog>["stepTypes"][number];

// The code-defined catalog (step types + trigger sources). Cached aggressively
// — it only changes when the API ships new types.
export function useAutomationCatalog() {
  const { data, loading, error } = useQuery(GetAutomationCatalogDocument, {
    fetchPolicy: "cache-first",
  });

  return {
    stepTypes: data?.automationStepTypes ?? [],
    triggerSources: data?.automationTriggerSources ?? [],
    isLoading: loading,
    error,
  };
}

// JSON scalars come back typed as Record<string, any>; the server actually
// returns a ConfigField[]. Narrow it at the consumption boundary.
export function asConfigSchema(value: unknown): AutomationConfigField[] {
  return Array.isArray(value) ? (value as AutomationConfigField[]) : [];
}
